import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Tooltip, withStyles } from '@material-ui/core';

export default function OverflowTooltipContent({
    text = '',
    className = '',
    style = {},
    placement = 'top',
    tooltipMaxWidth = 300 // default
}) {
    const textRef = useRef(null);
    const [isOverflow, setIsOverflow] = useState(false);
    const [computedMaxWidth, setComputedMaxWidth] = useState(tooltipMaxWidth);

    const checkOverflow = useCallback(() => {
        const el = textRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            const hasOverflow = el.scrollWidth > el.clientWidth;
            setIsOverflow(hasOverflow);
        });
    }, []);

    // ✅ Auto adjust tooltip max width if too big
    useEffect(() => {
        const screenWidth = window.innerWidth;
        const maxAllowed = screenWidth * 0.9;
        if (tooltipMaxWidth > maxAllowed) {
            setComputedMaxWidth(maxAllowed);
        } else {
            setComputedMaxWidth(tooltipMaxWidth);
        }
    }, [tooltipMaxWidth]);

    useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        checkOverflow();

        const observer = new ResizeObserver(() => checkOverflow());
        observer.observe(el);
        if (el.parentElement) observer.observe(el.parentElement);
        window.addEventListener('resize', checkOverflow);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [checkOverflow, text]);

    const CustomTooltip = withStyles({
        tooltip: {
            maxWidth: `${computedMaxWidth}px`,
            whiteSpace: 'normal',
            wordBreak: 'break-word'
        }
    })(Tooltip);

    const content = (
        <span
            ref={textRef}
            className={className}
            style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
                ...style
            }}
        >
            {text}
        </span>
    );

    return isOverflow ? (
        <CustomTooltip
            title={text}
            placement={placement}
            arrow
            disableHoverListener={false}
            disableTouchListener={false}
            disableFocusListener={false}
            enterDelay={0}
            enterTouchDelay={0}
        >
            {content}
        </CustomTooltip>
    ) : (
        content
    );
}
